import { useEffect, useMemo, useRef, type ReactNode } from "react"

import type { BrowseFilter, CampaignType, Currency, ExchangeRates, List, Platform, Talent } from "@/shared/types"
import { type TableColumn } from "@/components/tableKit"
import { TalentBrowseTable } from "@/components/talentBrowse/TalentBrowseTable"
import { TalentBrowseToolbar } from "@/components/talentBrowse/TalentBrowseToolbar"
import { countActiveFilters } from "@/components/talentBrowse/lib/filter"
import { talentColumns, type TalentRow, type TalentRowStatus, type TalentSelection } from "@/components/talentBrowse/talentColumns"
import { useTalentBrowse } from "@/components/talentBrowse/useTalentBrowse"
import { useSkeletonPhases } from "@/hooks/useSkeletonPhases"

export type { TalentRow, TalentSelection }

export type TalentBrowseEmptyMeta = {
    search: string
    filterCount: number
}

export type TalentRowState = {
    status?: TalentRowStatus
    pitcherName?: string
    selectable?: boolean
    price?: number | null
    priceInvalid?: boolean
}

export type TalentBrowseFilterSheet = {
    showPriceChips?: boolean
    currency?: Currency
    countryList?: string[]
    tagList?: string[]
    cityList?: string[]
    disabled?: boolean
}

export type TalentBrowseProps = {
    talent: Talent[]
    lists: List[]
    platforms: Platform[]
    campaignType: CampaignType
    currency: Currency
    exchangeRates: ExchangeRates
    selected?: TalentSelection
    onSelectionChange?: (selected: TalentSelection) => void
    defaultListId?: string
    defaultSortBy?: string
    defaultFilters?: BrowseFilter
    onListIdChange?: (listId: string) => void
    filterSheet?: TalentBrowseFilterSheet
    hidePrices?: boolean
    onFiltersChange?: (value: BrowseFilter) => void
    rowState?: (talent: Talent) => TalentRowState
    empty?: string | ((meta: TalentBrowseEmptyMeta) => ReactNode)
    columns?: TableColumn<TalentRow>[]
    requirePlatformMembership?: boolean
    isLoading?: boolean
}

/**
 * TalentBrowse is smart about browse, dumb about the page.
 * It owns search / list choice / sort / chips, checkbox↔price, guide/list price stamping, and the notes column when the current list is booker-viewable.
 * It does not fetch talent, pick platforms, keep the cart, or render TalentBrowseCampaignConstraints / PitchCart / BookingSummary.
 *
 * @param talent Fetched roster, not priced. Browse stamps guide/list prices.
 * @param lists Switcher options the page assembled.
 * @param platforms Page constraint. Browse does not pick these.
 * @param campaignType `"Song" | "Brand"`. Columns and list pricing.
 * @param currency Page currency. Same value as filterSheet.currency.
 * @param exchangeRates Page rates. Used to stamp localPrices.
 * @param selected Page-owned record. Omit when the table is display-only.
 * @param onSelectionChange Next full record, not a row patch.
 * @param defaultListId Initial list only.
 * @param defaultSortBy Initial sort (list order on a URL list).
 * @param defaultFilters Seeded chips (pitching eligibility).
 * @param onListIdChange Page hook when the list changes (New Booking lock).
 * @param filterSheet Sheet options. Omit to hide the filter button and sheet.
 * @param hidePrices Omit the price column (Public List hidePublicPrices).
 * @param onFiltersChange Latest sheet value (Edit List save). Browse still owns chips.
 * @param rowState Per-row exceptions (status, draft price). Omit on New Booking.
 * @param empty String or `(meta) => node`. Page-owned copy.
 * @param columns Override table columns. Default remains talentColumns.
 * @param requirePlatformMembership When false, skip the username-on-any-platform eligibility check. Default true.
 * @param isLoading Parent query has not produced a talent array yet, including `useFreshTalent` while it is still slicing. Combined with browse `pending` only when there are no rows yet; filter/All with previous rows stays pending-dim, not a new skeleton.
 */
export function TalentBrowse({
    talent,
    lists,
    platforms,
    campaignType,
    currency,
    exchangeRates,
    selected,
    onSelectionChange,
    defaultListId,
    defaultSortBy,
    defaultFilters,
    onListIdChange,
    filterSheet,
    hidePrices,
    onFiltersChange,
    rowState,
    empty,
    columns: columnsOverride,
    requirePlatformMembership,
    isLoading,
}: TalentBrowseProps) {
    const selectedIds = useMemo(() => new Set(Object.keys(selected ?? {})), [selected])
    const browse = useTalentBrowse({
        talent,
        lists,
        platforms,
        campaignType,
        currency,
        exchangeRates,
        selectedIds,
        defaultListId,
        defaultSortBy,
        defaultFilters,
        requirePlatformMembership,
    })
    const rows: TalentRow[] = browse.talent.map((tal) => {
        const extra = rowState?.(tal) ?? {}
        const status = extra.status ?? "available"
        const selectedEntry = selected?.[tal.id]
        return {
            talent: tal,
            status,
            pitcherName: extra.pitcherName,
            selected: selectedEntry != null,
            price: extra.price ?? selectedEntry?.price ?? null,
            guidePrice: tal.listCustomPrice || tal.defaultPrice || 0,
            selectable: extra.selectable ?? status === "available",
            priceInvalid: extra.priceInvalid,
        }
    })
    // Nothing to paint yet: the parent is still loading, or this mount has no rows while prices/filters are still pending. An already-finished empty parent (public list, pitching roster, New Booking after a platform) is not waiting.
    const waiting = Boolean(isLoading) || (rows.length === 0 && browse.pending)
    const phase = useSkeletonPhases(waiting)

    function handleListIdChange(nextListId: string) {
        browse.setListId(nextListId)
        onListIdChange?.(nextListId)
    }

    function handleRowSelectionChange(talentId: string, entry: { price: number } | null) {
        if (!onSelectionChange) {
            return
        }
        const next: TalentSelection = { ...selected }
        if (entry) {
            next[talentId] = entry
        } else {
            delete next[talentId]
        }
        onSelectionChange(next)
    }

    function handleFiltersChange(next: BrowseFilter) {
        browse.setFilters(next)
        onFiltersChange?.(next)
    }

    const hasReportedFilters = useRef(false)
    useEffect(() => {
        if (!onFiltersChange) {
            return
        }
        if (!hasReportedFilters.current) {
            hasReportedFilters.current = true
            return
        }
        onFiltersChange(browse.filters)
    }, [browse.filters, onFiltersChange])

    const currentList = lists.find((list) => list.id === browse.listId)
    const showNotes = Boolean(currentList?.notesBookerViewable) && browse.talent.some((tal) => tal.listNotes)
    const columns = columnsOverride ?? talentColumns({
        platforms,
        campaignType,
        currency: filterSheet?.currency ?? currency,
        onSelectionChange: onSelectionChange ? handleRowSelectionChange : undefined,
        showNotes,
        hidePrices,
    })

    const filterCount = filterSheet ? countActiveFilters(browse.filters, Boolean(filterSheet.showPriceChips)) : 0
    const emptyMeta = { search: browse.search, filterCount }
    const emptyNode = typeof empty === "function" ? empty(emptyMeta) : empty
    const emptyContent =
        emptyNode == null || typeof emptyNode === "string" ? (
            <div className="px-4 py-16 text-center italic">{emptyNode || "No creators match your filters."}</div>
        ) : (
            emptyNode
        )
    // First-attach-empty is `loading` again, so `showResult` here already has rows or a finished empty state.
    const isShowingTable = phase !== "showNothing"

    return (
        <>
            <TalentBrowseToolbar
                search={browse.search}
                onSearchChange={browse.setSearch}
                lists={lists}
                listId={browse.listId}
                onListIdChange={handleListIdChange}
                sortOptions={browse.sortOptions}
                sortBy={browse.sortBy}
                onSortByChange={browse.setSortBy}
                filters={
                    filterSheet
                        ? {
                            open: browse.filtersOpen,
                            onOpenChange: browse.setFiltersOpen,
                            value: browse.filters,
                            onChange: handleFiltersChange,
                            platforms,
                            showPriceChips: filterSheet.showPriceChips,
                            currency: filterSheet.currency,
                            countryList: filterSheet.countryList,
                            tagList: filterSheet.tagList,
                            cityList: filterSheet.cityList,
                            disabled: filterSheet.disabled,
                        }
                        : undefined
                }
            />
            {isShowingTable ? (
                <TalentBrowseTable columns={columns} rows={rows} pending={browse.pending} empty={phase === "showResult" && !browse.pending ? emptyContent : null} isLoading={phase === "showSkeleton"} />
            ) : null}
        </>
    )
}
